import type { IIncentiveRequestApiResponse } from "@/types/incentiveRequestApiTypes";
import type { IIncentiveRequest } from "@/types/requestTypes";
import type { IApprovalRequestApiResponse } from "@/types/approvalTypes";
import type { IApprovalRequest } from "@/types/approvalTypes";
import { apiService } from "@/services/apiService";
import { mapApiResponsesToIncentiveRequests } from "@/utils/incentiveRequestMapper";
import { mapApiResponsesToApprovalRequests } from "@/utils/approvalMapper";

async function getMyRequests(signal?: AbortSignal): Promise<IIncentiveRequest[]> {
  const instance = await apiService.getInstance();
  const response = await instance.get<IIncentiveRequestApiResponse[]>("/api/IncentiveRequests", { signal });
  return mapApiResponsesToIncentiveRequests(response.data);
}

async function getMyApprovals(signal?: AbortSignal): Promise<IApprovalRequest[]> {
  const instance = await apiService.getInstance();
  const response = await instance.get<IApprovalRequestApiResponse[]>("/api/IncentiveRequests/my-approvals", { signal });
  return mapApiResponsesToApprovalRequests(response.data);
}

export interface IFileDownloadResult {
  blob: Blob;
  fileName: string;
}

/**
 * Downloads the modeler file for the given incentive request.
 * Returns the file as a Blob with the filename from the Content-Disposition header.
 */
async function downloadFile(requestId: string): Promise<IFileDownloadResult> {
  const instance = await apiService.getInstance();
  const response = await instance.get(
    `/api/IncentiveRequests/${encodeURIComponent(requestId)}/download`,
    { responseType: "blob" },
  );

  const contentDisposition = response.headers["content-disposition"] ?? "";
  const fileNameMatch = contentDisposition.match(/filename\*?=['"]?(?:UTF-8'')?([^;'"\n]+)/i);
  const fileName = fileNameMatch ? decodeURIComponent(fileNameMatch[1].trim()) : "download.xlsx";

  return { blob: response.data as Blob, fileName };
}

async function submitApprovalAction(
  approvalId: string,
  action: "Approve" | "Reject",
): Promise<void> {
  const instance = await apiService.getInstance();
  await instance.post(`/api/IncentiveRequests/${approvalId}/review`, {
    isApproved: action === "Approve",
    comments: null,
  });
}

export const incentiveRequestService = {
  getMyRequests,
  getMyApprovals,
  submitApprovalAction,
  downloadFile,
};
